import { useState, useEffect, useMemo } from 'react';
import Cookies from 'universal-cookie';
import axiosInstance from '../api/axiosInstance';
import { API_BASE_URL } from '../api/apiConfig';
import { isSubscriptionCountryStripeUnsupported } from '../utils/apexPayAsYouGoBanner';
import { getCustomerCountryCode, resolveDisplayPricingContext } from '../views/pages/planAndBilling/constants/subscriptionPricingRegion';

const cookies = new Cookies();

/**
 * Hook to fetch subscription plan details
 * Plans are determined by frequency field from API:
 * - frequency: 0 = monthly
 * - frequency: 1 = 1year
 * - frequency: 3 = 3years
 * - frequency: 4 = 5years
 * @param {boolean} skip - If true, skip the API call (useful when data is passed as props)
 * @returns {Object} Subscription plans data and loading/error states
 */
export const useSubscriptionPlans = (skip = false) => {
  const [plans, setPlans] = useState({
    monthly: null,
    '1year': null,
    '3years': null,
    '5years': null
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [isCountrySupported, setIsCountrySupported] = useState(true); // Default to true

  useEffect(() => {
    // Skip API call if skip flag is set (data will be provided via props)
    if (skip) {
      setLoading(false);
      return () => {
        // No cleanup needed when skip is true
      };
    }
    
    let isMounted = true;
    const abortController = new AbortController();
    
    const fetchPlans = async () => {
      try {
        if (!isMounted) return;
        setLoading(true);
        setError(null);

        const userAccountId = cookies.get('userAccountId') || cookies.get('accountId');
        const _Id = cookies.get('_Id');

        if (!userAccountId || !_Id) {
          throw new Error('Missing required authentication parameters');
        }

        // Fetch plans - API returns all plans in one response
        // Use "true" instead of planId in the URL
        try {
          const url = `${API_BASE_URL}user/v2/subscriptionPlans/${_Id}`;
          
          const response = await axiosInstance({
            method: 'get',
            url,
            headers: {
              token: localStorage.getItem('token'),
              platform: 'WEB',
              currentVer: '',
              accountId: cookies.get('accountId'),
              apiKey: process.env.apiKey
            }
          });
          
          if (!isMounted) return;

          const apiData = response?.data?.details;

          // Extract plans from getPlanList.plans array
          const plansData = {};
          const frequencyMap = { '0': 'monthly', '1': '1year', '3': '3years', '4': '5years' };
          
          if (apiData?.getPlanList?.plans && Array.isArray(apiData.getPlanList.plans)) {
            apiData.getPlanList.plans.forEach(plan => {
              const key = frequencyMap[plan.frequency];
              if (key) {
                plansData[key] = plan;
              }
            });
          }

          // Extract userInfo from top level
          // Note: "Netgear Partner" is now in getPlanList, not userInfo
          const extractedUserInfo = apiData?.userInfo || {};
          
          // Extract "Netgear Partner" from getPlanList and add it to userInfo
          if (apiData?.getPlanList?.["Netgear Partner"]) {
            extractedUserInfo["Netgear Partner"] = apiData.getPlanList["Netgear Partner"];
          }

          // Extract isCountrySupported from getPlanList (default supported when field absent)
          const countrySupported = !isSubscriptionCountryStripeUnsupported(
            apiData?.getPlanList?.isCountrySupported
          );

          if (!isMounted) return;

          setPlans(plansData);

          setIsCountrySupported(countrySupported);
          if (Object.keys(extractedUserInfo).length > 0) {
            setUserInfo(extractedUserInfo);
          }
        } catch (err) {
          if (!isMounted) return;
          throw err;
        }
      } catch (err) {
        // Ignore abort errors
        if (err.name === 'AbortError' || err.name === 'CanceledError') {
          return;
        }
        
        if (!isMounted) return;
        
        setError(err.message);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchPlans();
    
    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, [skip]);

  const customerCountry = getCustomerCountryCode();
  const displayPricingContext = useMemo(
    () => resolveDisplayPricingContext(plans),
    [plans, customerCountry]
  );

  return {
    plans,
    loading,
    error,
    userInfo,
    isCountrySupported,
    pricingCategory: displayPricingContext.displayPricingCategory,
    displayPricingContext,
  };
};

export default useSubscriptionPlans;

